import axios from 'axios';
import { apiBaseUrl } from './apiBase';

export interface WildfireHotspot {
    id: string;
    name: string;
    lat: number;
    lon: number;
    confidence: 'low' | 'medium' | 'high';
    intensity: number;
    observed: string;
}

interface WildfireApiItem {
    id?: string | number;
    latitude?: number | string;
    longitude?: number | string;
    confidence?: number | string;
    frp?: number | string;
    acq_date?: string;
    acq_time?: string;
    satellite?: string;
    source?: string;
}

const cache = new Map<string, { expiresAt: number; data: WildfireHotspot[] }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

function normalizeConfidence(value: unknown): WildfireHotspot['confidence'] {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        if (numeric >= 80) return 'high';
        if (numeric >= 50) return 'medium';
    }

    const text = String(value ?? '').toLowerCase();
    if (text.includes('high')) return 'high';
    if (text.includes('medium') || text.includes('nominal')) return 'medium';
    return 'low';
}

function normalizeIntensity(value: unknown): number {
    const frp = Number(value);
    if (!Number.isFinite(frp) || frp <= 0) return 0.2;
    return Math.max(0.15, Math.min(1, frp / 500));
}

function toHotspot(item: WildfireApiItem, index: number): WildfireHotspot | null {
    const lat = Number(item.latitude);
    const lon = Number(item.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return {
        id: String(item.id ?? `${lat.toFixed(4)}_${lon.toFixed(4)}_${index}`),
        name: item.satellite ? `NASA FIRMS ${item.satellite}` : 'NASA FIRMS hotspot',
        lat,
        lon,
        confidence: normalizeConfidence(item.confidence),
        intensity: normalizeIntensity(item.frp),
        observed: [item.acq_date, item.acq_time].filter(Boolean).join(' ') || item.source || 'NASA FIRMS',
    };
}

function normalizeLongitude(value: number): number {
    return ((((value + 180) % 360) + 360) % 360) - 180;
}

// نطابق نفس منطق weatherGridService: MapLibre مع renderWorldCopies يُرجع أحياناً
// خطوط طول خارج ±180 عند التكبير بعيداً (مثل east=232 بدل -128) — الباك إند يرفضها
// بـ422، فنُطبِّعها هنا قبل الإرسال.
function normalizeBounds(bounds: { north: number; south: number; east: number; west: number }) {
    const north = Math.min(90, Math.max(-90, Math.max(bounds.north, bounds.south)));
    const south = Math.min(90, Math.max(-90, Math.min(bounds.north, bounds.south)));
    const rawSpan = Math.abs(bounds.east - bounds.west);
    let west = normalizeLongitude(bounds.west);
    let east = normalizeLongitude(bounds.east);

    if (rawSpan >= 330 || east <= west) {
        west = -180;
        east = 180;
    }

    return {
        north: Number(north.toFixed(1)),
        south: Number(south.toFixed(1)),
        east: Number(east.toFixed(1)),
        west: Number(west.toFixed(1)),
    };
}

function cacheKey(bounds: { north: number; south: number; east: number; west: number }): string {
    return `${bounds.north}:${bounds.south}:${bounds.east}:${bounds.west}`;
}

export async function getWildfiresForBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
}): Promise<WildfireHotspot[]> {
    const normalized = normalizeBounds(bounds);
    const key = cacheKey(normalized);
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.data;

    const response = await axios.get(`${apiBaseUrl}/wildfires/region`, {
        params: { ...normalized, limit: 500 },
        // النداء الأول لـ NASA FIRMS البارد يتجاوز 15 ثانية قبل التخزين المؤقت —
        // كانت المهلة 15 ثانية فتفشل كل الطلبات الأولى ولا تظهر أي حرائق.
        timeout: 45000,
    });

    const fires = Array.isArray(response.data?.fires) ? response.data.fires : [];
    const data = fires
        .map((item: WildfireApiItem, index: number) => toHotspot(item, index))
        .filter((item: WildfireHotspot | null): item is WildfireHotspot => item !== null);
    cache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, data });
    if (cache.size > 48) {
        const oldestKey = cache.keys().next().value;
        if (oldestKey) cache.delete(oldestKey);
    }
    return data;
}
